"use client";

import { useState, useMemo } from "react";
import { useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RecipientSelector } from "./recipient-selector";
import { CcSelector } from "./cc-selector";
import { AttachmentPicker } from "./attachment-picker";
import { getRecipientById, getRecipientByEmail, EmailRecipient } from "@/lib/email-recipients";
import { useGmailAuth } from "@/hooks/use-gmail-auth";
import { GmailConnectButton } from "./gmail-connect-button";
import { Send, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface EmailComposerProps {
  blockId?: Id<"blocks">;
  defaultSubject?: string;
  defaultRecipientEmail?: string;
  defaultRecipientName?: string;
  onSent?: () => void;
}

export function EmailComposer({ blockId, defaultSubject, defaultRecipientEmail, defaultRecipientName, onSent }: EmailComposerProps) {
  const { connection } = useGmailAuth();
  const { toast } = useToast();
  const sendEmail = useAction(api.email.send.sendEmail);

  // Create a custom recipient for reply-to addresses not in predefined list
  const customRecipient = useMemo<EmailRecipient | undefined>(() => {
    if (!defaultRecipientEmail) return undefined;
    // Check if this email is already in predefined recipients
    const existing = getRecipientByEmail(defaultRecipientEmail);
    if (existing) return undefined;
    // Create a custom recipient for reply
    return {
      id: `reply-${defaultRecipientEmail}`,
      email: defaultRecipientEmail,
      name: defaultRecipientName || defaultRecipientEmail,
      description: "Reply to this sender",
      category: "amazon" as const,
    };
  }, [defaultRecipientEmail, defaultRecipientName]);

  // Find recipient ID from email if provided
  const defaultRecipient = defaultRecipientEmail ? getRecipientByEmail(defaultRecipientEmail) : undefined;
  const initialRecipientId = defaultRecipient?.id ?? customRecipient?.id;
  const [recipientId, setRecipientId] = useState<string | undefined>(initialRecipientId);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState(defaultSubject ?? "");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<
    Array<{ id: string; filename: string; mimeType: string; dataUrl: string; size: number }>
  >([]);
  const [isSending, setIsSending] = useState(false);

  // Get selected recipient for excluding from CC (check custom recipient too)
  const selectedRecipient = recipientId
    ? (getRecipientById(recipientId) ?? (customRecipient?.id === recipientId ? customRecipient : null))
    : null;

  const handleSend = async () => {
    const recipient = recipientId
      ? (getRecipientById(recipientId) ?? (customRecipient?.id === recipientId ? customRecipient : null))
      : null;
    if (!recipient) {
      toast({ title: "Select a recipient", variant: "destructive" });
      return;
    }
    if (!subject.trim()) {
      toast({ title: "Enter a subject", variant: "destructive" });
      return;
    }
    if (!body.trim()) {
      toast({ title: "Enter a message", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      // Convert data URLs to base64
      const formattedAttachments = attachments.map((a) => ({
        filename: a.filename,
        mimeType: a.mimeType,
        data: a.dataUrl.split(",")[1], // Remove data:image/jpeg;base64, prefix
      }));

      await sendEmail({
        recipientEmail: recipient.email,
        recipientName: recipient.name,
        cc: ccEmails.length > 0 ? ccEmails : undefined,
        subject,
        body: `<html><body>${body.replace(/\n/g, "<br>")}</body></html>`,
        attachments: formattedAttachments,
        relatedBlockId: blockId,
      });

      toast({ title: "Email sent!" });
      onSent?.();
    } catch (error) {
      toast({
        title: "Failed to send",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!connection) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-center text-muted-foreground">
          Connect your Gmail account to send emails
        </p>
        <GmailConnectButton />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <RecipientSelector value={recipientId} onValueChange={setRecipientId} customRecipient={customRecipient} />

      <CcSelector
        value={ccEmails}
        onValueChange={setCcEmails}
        excludeEmail={selectedRecipient?.email}
      />

      <div className="space-y-2">
        <label className="text-sm font-medium">Subject</label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Email subject..."
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Message</label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your message..."
          className="min-h-[200px]"
        />
      </div>

      <AttachmentPicker
        blockId={blockId}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
      />

      <Button onClick={handleSend} disabled={isSending} className="w-full">
        {isSending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Send className="h-4 w-4 mr-2" />
        )}
        Send Email
      </Button>
    </div>
  );
}
